import { authService } from './authService';
import { apiFetch } from './apiFetch';
import { ACTIVE_URL } from '../config';
import { parseErrorResponse } from '../utils/errorHandling';

const API_BASE_URL = `${ACTIVE_URL}/api/v1/vms`;

const getHeaders = (isFormData = false) => {
  const token = authService.getAccessToken();
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`
  };
  
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  
  return headers;
};

const handleResponse = async (response: Response) => {
  if (!response.ok) {
    throw await parseErrorResponse(response);
  }
  
  if (response.status === 204) {
    return null;
  }
  
  return response.json();
};

export const vmsService = {
  // --- Vendors ---
  async getVendors(filters?: { search?: string; name?: string; code?: string; state?: string; vendor_category?: string; nature_of_service?: string; department?: number | string; vendor_type?: number | string; status?: string; page?: number; page_size?: number }) {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.name) params.append('name', filters.name);
    if (filters?.code) params.append('code', filters.code);
    if (filters?.state) params.append('state', filters.state);
    if (filters?.vendor_category) params.append('vendor_category', filters.vendor_category);
    if (filters?.nature_of_service) params.append('nature_of_service', filters.nature_of_service);
    if (filters?.department) params.append('department', String(filters.department));
    if (filters?.vendor_type) params.append('vendor_type_id', String(filters.vendor_type));
    if (filters?.status) params.append('status', filters.status);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.page_size) params.append('page_size', String(filters.page_size));
    
    const response = await apiFetch(`${API_BASE_URL}/vendors/?${params.toString()}`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async getGlobalInvoices() {
    const response = await apiFetch(`${API_BASE_URL}/invoices/`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async getVendorComparison(filters?: { department_id?: string | number; vendor_type_id?: string | number; state?: string; date_from?: string; date_to?: string; product_id?: string | number }) {
    const params = new URLSearchParams();
    if (filters?.department_id) params.append('department_id', String(filters.department_id));
    if (filters?.vendor_type_id) params.append('vendor_type_id', String(filters.vendor_type_id));
    if (filters?.state) params.append('state', filters.state);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.product_id) params.append('product_id', String(filters.product_id));
    
    const response = await apiFetch(`${API_BASE_URL}/vendors/compare/?${params.toString()}`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async getVendor(id: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${id}/`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async createVendor(data: Record<string, any>) {
    const hasFiles = Object.values(data).some(val => val instanceof File);
    let body: any = JSON.stringify(data);
    let isFormData = false;

    if (hasFiles) {
      isFormData = true;
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
          formData.append(key, data[key]);
        }
      });
      body = formData;
    }

    const response = await apiFetch(`${API_BASE_URL}/vendors/`, {
      method: 'POST',
      headers: getHeaders(isFormData),
      body: body,
    });
    return handleResponse(response);
  },

  async updateVendor(id: string | number, data: Record<string, any>) {
    const hasFiles = Object.values(data).some(val => val instanceof File);
    let body: any = JSON.stringify(data);
    let isFormData = false;

    if (hasFiles) {
      isFormData = true;
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
          // don't append URL strings to File fields on update if they aren't new Files
          if (typeof data[key] === 'string' && (key === 'nda_document' || key === 'agreement_document')) {
            return;
          }
          formData.append(key, data[key]);
        }
      });
      body = formData;
    }

    const response = await apiFetch(`${API_BASE_URL}/vendors/${id}/`, {
      method: 'PATCH',
      headers: getHeaders(isFormData),
      body: body,
    });
    return handleResponse(response);
  },

  async deleteVendor(id: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${id}/`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  async reactivateVendor(id: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${id}/reactivate/`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // --- Vendor Types ---
  async getVendorTypes() {
    const response = await apiFetch(`${API_BASE_URL}/vendor-types/`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async createVendorType(data: Record<string, any>) {
    const response = await apiFetch(`${API_BASE_URL}/vendor-types/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async updateVendorType(id: string | number, data: Record<string, any>) {
    const response = await apiFetch(`${API_BASE_URL}/vendor-types/${id}/`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // --- Vendor Doc Types ---
  async getDocTypes() {
    const response = await apiFetch(`${API_BASE_URL}/doc-types/`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async createDocType(data: Record<string, any>) {
    const response = await apiFetch(`${API_BASE_URL}/doc-types/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // --- Commercial Input Types ---
  async getCommercialInputTypes() {
    const response = await apiFetch(`${API_BASE_URL}/commercial-input-types/`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async createCommercialInputType(data: Record<string, any>) {
    const response = await apiFetch(`${API_BASE_URL}/commercial-input-types/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // --- Vendor Doc Details ---
  async getVendorDocDetails(vendorId: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/doc-details/`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async createVendorDocDetail(vendorId: string | number, data: Record<string, any>) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/doc-details/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async updateVendorDocDetail(vendorId: string | number, detailId: string | number, data: Record<string, any>) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/doc-details/${detailId}/`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async deleteVendorDocDetail(vendorId: string | number, detailId: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/doc-details/${detailId}/`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  async downloadCurrentVendorDoc(vendorId: string | number, detailId: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/doc-details/${detailId}/download/`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // --- Vendor Doc Versions ---
  async getVendorDocVersions(vendorId: string | number, detailId: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/doc-details/${detailId}/versions/`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async uploadVendorDocVersion(vendorId: string | number, detailId: string | number, formData: FormData) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/doc-details/${detailId}/versions/`, {
      method: 'POST',
      headers: getHeaders(true),
      body: formData,
    });
    return handleResponse(response);
  },

  async downloadVendorDocVersion(vendorId: string | number, detailId: string | number, versionId: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/doc-details/${detailId}/versions/${versionId}/download/`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  async shareVendorDocVersion(vendorId: string | number, detailId: string | number, versionId: string | number, recipientEmail: string) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/doc-details/${detailId}/versions/${versionId}/share/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ recipient_email: recipientEmail }),
    });
    return handleResponse(response);
  },

  // --- Products ---
  async getProducts(department_id?: string | number) {
    let url = `${API_BASE_URL}/products/`;
    if (department_id) url += `?department_id=${department_id}`;
    const response = await apiFetch(url, { headers: getHeaders() });
    return handleResponse(response);
  },

  async createProduct(data: Record<string, any>) {
    const response = await apiFetch(`${API_BASE_URL}/products/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // --- Vendor Products ---
  async getVendorProducts(vendorId: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/products/`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async createVendorProduct(vendorId: string | number, data: Record<string, any>) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/products/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async updateVendorProduct(vendorId: string | number, productId: string | number, data: Record<string, any>) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/products/${productId}/`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async deleteVendorProduct(vendorId: string | number, productId: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/products/${productId}/`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // --- Vendor Bank Details ---
  async getVendorBanks(vendorId: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/banks/`, { headers: getHeaders() });
    return handleResponse(response);
  },

  async createVendorBank(vendorId: string | number, data: Record<string, any>) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/banks/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async updateVendorBank(vendorId: string | number, bankId: string | number, data: Record<string, any>) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/banks/${bankId}/`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async archiveVendorBank(vendorId: string | number, bankId: string | number) {
    const response = await apiFetch(`${API_BASE_URL}/vendors/${vendorId}/banks/${bankId}/`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse(response);
  }
};
